<template>
  <ValidationProvider
      v-slot="{ errors, classes}"
      :class="$attrs.class"
      :mode="customValidate"
      :rules="rules"
      class="form-group w-full v-select" tag="div"
  >
    <label v-if="label" :class="labelClass" class="block mb-1">
      {{ label }}
      <b v-if="isRequired" class="ml-1 text-red-600 font-bold">*</b>
    </label>
    <div class="relative input-group">
      <VueSelect
          ref="select"
          v-model="dropdownValue"
          :append-to-body="true"
          :autoscroll="true"
          :clearable="clearable"
          :deselectFromDropdown="multiple"
          :disabled="disabled"
          :filterable="false"
          :get-option-key="option => option?.id"
          :get-option-label="option => option && option[fieldLabel] ? option[fieldLabel] : ''"
          :label="fieldLabel"
          :multiple="multiple"
          :options="paginated"
          :placeholder="placeholder"
          :pushTags="true"
          :reduce="option => !returnObj ? option?.id : option"
          :taggable="taggable"
          class="form-control"
          :class="hasData ? 'has-data' : ''"
          v-bind="$attrs"
          @close="onClose"
          @open="onOpen"
          @search="onSearch"
          v-on="$listeners"
      >
        <template #option="{ id, text }">
          <div :class="[{'font-bold' : isSelectedOptionItem(id)}]">{{ text }}</div>
        </template>
        <template #no-options="{search, searching}">
          <div class="px-3 py-2 text-center">
            <span v-if="!loading"> {{ $t("No items in select") }}</span>
          </div>
        </template>
        <template #list-footer>
          <li v-show="loading" ref="load" class="list-footer">
            <i class="fa-solid fa-spin fa-circle-notch"></i> {{ $t('Data loading') }}
          </li>
        </template>
      </VueSelect>
    </div>
    <span v-if="!!hint" :class="hintClass" class="text-xs text-gray-400">{{ hint }}</span>

    <span class="text-xs text-red-600">{{ errors[0] }}</span>
  </ValidationProvider>
</template>

<script>
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import {$debounce, convertAccentedLettersToUnsigned, unique} from "@/helpers/helpers";


export default {
  name: "Select",
  components: {
    VueSelect
  },
  props: {
    fieldLabel: {
      type: String,
      default: "text"
    },
    value: [Number, String, Array, Object],
    label: String,
    multiple: {
      type: Boolean,
      default: false,
    },
    returnObj: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    search: {
      type: [Function, undefined],
      default: null,
    },
    placeholder: String,
    rules: String,
    params: {
      type: Object,
      default: () => {
      }
    },
    hintClass: [String, Array],
    hint: {
      type: String,
      default: ""
    },
    labelClass: {
      type: String,
      default: "text-base font-normal"
    }
  },
  data: () => ({
    isFirst: true,
    hasMore: true,
    observer: null,
    placement: "bottom",
    optionRef: null,
    valueType: "",
    limit: 20,
    items: [],
    keyword: "",
    loading: false,
    isClose: false,
  }),
  computed: {
    filtered() {
      const q = convertAccentedLettersToUnsigned(this.keyword.toLowerCase());
      return this.items.filter(item => convertAccentedLettersToUnsigned(item[this.fieldLabel].toLowerCase()).includes(q))
    },
    paginated() {
      return this.search ? this.items : this.filtered;
    },
    hasNextPage() {
      return this.paginated.length <= this.filtered.length;
    },
    isOptionsAllowNullOrEmpty() {
      return !!this.options.find(x => x.id === "" || x.id === 0);
    },
    dropdownValue: {
      get() {
        // return this.value;
        // if (this.isOptionsAllowNullOrEmpty) {
        //   return this.value;
        // }

        if (this.multiple) {
          return this.value;
        }

        return (this.value === 0 || this.value === "") && !this.isOptionsAllowNullOrEmpty ? null : this.value;
      },
      set(newDropdownValue) {
        if (newDropdownValue === null) {
          newDropdownValue = this.getDefaultValue(this.value)
        }
        this.$emit("input", newDropdownValue);
        const obj = this.paginated.find(x => x.id === newDropdownValue);
        this.$emit("change", obj || newDropdownValue);
      }
    },
    isRequired() {
      return this.required || this.rules?.search("required") > -1;
    },
    page() {
      const totalItems = this.items.length;
      // return totalItems === 0 ? 0 : totalItems / this.limit + 1;
      return totalItems === 0 ? 0 : Math.round(totalItems / this.limit);
    },
    hasData() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0;
      }
      return this.value !== null && this.value !== undefined && this.value !== "" && this.selectedLabel !== "";
    },
    selectedLabel() {
      if (this.multiple) return "";

      // Nếu value là object và có fieldLabel
      if (typeof this.value === "object" && this.value !== null) {
        return this.value[this.fieldLabel] || "";
      }

      // Nếu value là id (số hoặc chuỗi), tìm trong items/options
      const selectedItem = this.items.find(item => item.id?.toString() === this.value?.toString());
      return selectedItem ? selectedItem[this.fieldLabel] : "";
    }
  },
  created() {
    this.valueType = typeof this.value;
  },
  mounted() {
    this.observer = new IntersectionObserver(this.infiniteScroll);
  },
  watch: {
    options: {
      handler(newVal) {
        if (!newVal.length) return;
        this.items = unique([...this.items, ...newVal]);
        const selected = this.items.find(x => x.selected);
        if (!selected) return;
        this.dropdownValue = selected.id;
      },
      immediate: true
    },
    search() {
      this.isFirst = true;
    }
  },
  methods: {
    customValidate() {
      return {on: ["search:blur", "input"]};
    },
    async onSearch(search, loading) {
      /* Do thư viện này bị ngu, tự gọi onSearch khi close nên cần check isClose để bỏ qua*/
      if (this.isClose || !this.search) return;

      if (search !== this.keyword) {
        this.items = [];
        this.hasMore = true;
        this.keyword = search;
      }

      await $debounce(300);
      // loading(true);
      await this.fetchOptions();
      // loading(false);
    },
    async fetchOptions() {
      if (!this.hasMore || this.loading) return;
      this.loading = true;
      const response = await this.search({
        keyword: this.keyword,
        take: this.limit,
        page: this.page,
        ...this.params,
      });
      this.loading = false;
      if (response.error) return;
      this.hasMore = response.data.data.length === this.limit;

      this.items = unique([...this.items, ...response.data.data]);
    },
    async infiniteScroll([{isIntersecting, target}]) {

      if (isIntersecting) {
        const ul = target.offsetParent;
        const scrollTop = target.offsetParent.scrollTop;
        if (!this.search) {
          this.limit += 10;
        }
        await this.$nextTick();
        ul.scrollTop = scrollTop;
      }

    },
    async onScroll(e) {
      const {scrollTop, offsetHeight, scrollHeight} = e.target
      if ((scrollTop + offsetHeight) >= scrollHeight) {
        await $debounce(300);
        if (this.search)
        {
          await this.fetchOptions();
        }
      }
    },
    async onOpen() {
      this.isClose = false;
      await this.$nextTick();
      this.optionRef = this.$refs.select.$refs.dropdownMenu;
      this.optionRef.addEventListener("scroll", this.onScroll)

      if (this.hasNextPage) {
        this.observer.observe(this.$refs.load);
      }

      // if (this.search && (this.isFirst || this.keyword !== "")) {
      if (this.search) {
        this.keyword = "";
        this.items = [];
        this.hasMore = true;
        await this.fetchOptions();
      }
      this.isFirst = false;
    },
    onClose() {
      this.observer.disconnect();
      this.optionRef.removeEventListener("scroll", this.onScroll);
      this.optionRef = null;
      this.isClose = true;
    },
    isSelectedOptionItem(id) {
      if (this.valueType === "object") {
        if (this.value instanceof Array) {
          const selectedIds = this.value.map(x => x.id);
          return selectedIds.includes(id);
        }
        return this.value?.id?.toString() === id?.toString();
      }
      if (this.valueType === "string" || this.valueType === "number") {
        return this.value?.toString() === id?.toString();
      }
    },
    getDefaultValue(val) {
      let newVal;
      switch (typeof val) {
        case "number": {
          newVal = 0;
          break;
        }
        case "string": {
          newVal = "";
          break;
        }
        case "object": {
          if (this.multiple) {
            newVal = [];
          }
          break;
        }
      }
      return newVal;
    },
  }
}
</script>

<style>
.v-select input {
  cursor: pointer !important;
}
</style>